import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  isOrganizationLinkFooterActionSupported,
  validContentfulData as fallback,
} from '../../utils';

export function useOrganizationLinksFooterContentfulData() {
  const { data: possibleContentfulData } =
    useContentfulContentTypeEntry<ContentfulOrganizationLinksFooter>({
      contentType: ORGANIZATION_LINKS_FOOTER_CONTENT_ID,
      include: 2,
    });

  // ─── Live Contentful Footer Data ─────────────────────────────────────

  const liveFooter = convertFooter(possibleContentfulData?.fields);

  if (liveFooter.sections.length > 0) {
    return liveFooter;
  }

  // ─── Fallback Contentful Footer Data ─────────────────────────────────

  const fallbackFooter = convertFooter(fallback.fields);

  return fallbackFooter;
}

// ─── Converters ────────────────────────────────────────────────────────

function convertFooter(
  footer?: ContentfulOrganizationLinksFooter,
): ContentfulOrganizationLinksFooterData {
  const sections = footer?.sections ?? [];

  return { sections: sections.filter(hasSectionFields).map(convertSection) };
}

function convertSection(section: ContentfulOrganizationLinksFooterSection) {
  return {
    name: section.fields.name,
    links: section.fields.links.filter(isLinkValid).map(convertLink),
  };
}

function convertLink(link: ContentfulOrganizationLinksFooterLink) {
  return link.fields;
}

function hasSectionFields(section: ContentfulOrganizationLinksFooterSection) {
  return Boolean(section.fields);
}

function isLinkValid(link: ContentfulOrganizationLinksFooterLink) {
  const isActionUnsupported = Boolean(
    link?.fields?.specialHandler &&
      !isOrganizationLinkFooterActionSupported(link.fields.specialHandler),
  );

  return Boolean(link.fields) && !isActionUnsupported;
}

// ─── Types ─────────────────────────────────────────────────────────────

type ContentfulOrganizationLinksFooter = Readonly<{
  name: string;
  sections: readonly ContentfulOrganizationLinksFooterSection[];
}>;

type ContentfulOrganizationLinksFooterSection = Readonly<{
  fields: {
    name: string;
    links: readonly ContentfulOrganizationLinksFooterLink[];
  };
}>;

type ContentfulOrganizationLinksFooterLink = Readonly<{
  fields: {
    label: string;
    url: string;
    specialHandler?: 'kustomer';
  };
}>;

type ContentfulOrganizationLinksFooterData = Readonly<{
  sections: ReadonlyArray<
    Readonly<{
      name: string;
      links: ReadonlyArray<
        Readonly<{
          label: string;
          url: string;
          specialHandler?: 'kustomer';
        }>
      >;
    }>
  >;
}>;

// ─── Constants ─────────────────────────────────────────────────────────

const ORGANIZATION_LINKS_FOOTER_CONTENT_ID = 'organizationLinksFooter';
